import React, { useMemo } from 'react';
import ArrowRightIcon from '@atlaskit/icon/core/migration/arrow-right';
import {
	COMMAND_PALETTE_SECTIONS,
	COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY,
} from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { useTransformNavigationCommands } from '@atlassian/jira-command-palette-common/src/common/utils/transform-navigation-commands/index.tsx';
import type { CommandToTransform } from '@atlassian/jira-command-palette-common/src/common/utils/transform-navigation-commands/types.tsx';
import { useCommandKeywords } from '@atlassian/jira-command-palette-common/src/controllers/use-command-keywords/index.tsx';
import { COMMAND_PALETTE_REGISTRY_IDS } from '@atlassian/jira-command-palette-registry/src/common/constants/registry/index.tsx';
import { RegisterCommands } from '@atlassian/jira-command-palette-registry/src/ui/register-commands/index.tsx';
import type { Command } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav3.tsx';
import {
	BACKLOG_COMMAND_ID,
	BOARD_COMMAND_ID,
	REPORTS_COMMAND_ID,
	TIMELINE_COMMAND_ID,
} from './constants.tsx';
import messages from './messages.tsx';
import type { RegisterProjectNavigationCommandsProps } from './types.tsx';

export const RegisterProjectNavigationCommands = ({
	features,
	timelineHref,
	boardHref,
	backlogHref,
	reportsHref,
}: RegisterProjectNavigationCommandsProps) => {
	const { hideAndTransformNavigationCommands } = useTransformNavigationCommands();
	const { getKeywords } = useCommandKeywords();
	const { formatMessage } = useIntl();

	const hasTimeline = features?.hasRoadmap;
	const hasBacklog = features?.hasBacklog;
	const hasBoard = features?.hasBoard;
	const hasReports = features?.hasReports;

	const commands = useMemo(
		(): Command[] =>
			hideAndTransformNavigationCommands([
				...(getWillShowNav3() && hasTimeline
					? [
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							{
								command: {
									id: TIMELINE_COMMAND_ID,
									name: formatMessage(messages.commandPaletteGoToTimeline),
									section: COMMAND_PALETTE_SECTIONS.containerNavigation,
									keywords: getKeywords('timelineSynonyms'),
									shortcut: '4',
									priority: COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY.goToTimeline,
									components: {
										LeftIcon: () => (
											<ArrowRightIcon
												spacing="spacious"
												label={formatMessage(messages.commandPaletteGoToTimeline)}
											/>
										),
									},
									analytics: {
										action: 'goToTimeline',
									},
								},
								path: timelineHref,
							} as CommandToTransform,
						]
					: []),
				...(getWillShowNav3() && hasBacklog
					? [
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							{
								command: {
									id: BACKLOG_COMMAND_ID,
									name: formatMessage(messages.commandPaletteGoToBacklog),
									section: COMMAND_PALETTE_SECTIONS.containerNavigation,
									keywords: getKeywords('backlogSynonyms'),
									shortcut: '1',
									priority: COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY.goToBacklog,
									components: {
										LeftIcon: () => (
											<ArrowRightIcon
												spacing="spacious"
												label={formatMessage(messages.commandPaletteGoToBacklog)}
											/>
										),
									},
									analytics: {
										action: 'goToBacklog',
									},
								},
								path: backlogHref,
							} as CommandToTransform,
						]
					: []),
				...(getWillShowNav3() && hasBoard
					? [
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							{
								command: {
									id: BOARD_COMMAND_ID,
									name: formatMessage(messages.commandPaletteGoToBoard),
									section: COMMAND_PALETTE_SECTIONS.containerNavigation,
									keywords: getKeywords('boardSynonyms'),
									shortcut: '2',
									priority: COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY.goToBoard,
									components: {
										LeftIcon: () => (
											<ArrowRightIcon
												spacing="spacious"
												label={formatMessage(messages.commandPaletteGoToBoard)}
											/>
										),
									},
									analytics: {
										action: 'goToBoard',
									},
								},
								path: boardHref,
							} as CommandToTransform,
						]
					: []),
				...(getWillShowNav3() && hasReports
					? [
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							{
								command: {
									id: REPORTS_COMMAND_ID,
									name: formatMessage(messages.commandPaletteGoToReports),
									section: COMMAND_PALETTE_SECTIONS.containerNavigation,
									keywords: getKeywords('reportsSynonyms'),
									shortcut: '3',
									priority: COMMAND_PALETTE_PROJECT_NAVIGATION_PRIORITY.goToReports,
									components: {
										LeftIcon: () => (
											<ArrowRightIcon
												spacing="spacious"
												label={formatMessage(messages.commandPaletteGoToReports)}
											/>
										),
									},
									analytics: {
										action: 'goToReports',
									},
								},
								path: reportsHref,
							} as CommandToTransform,
						]
					: []),
			]),
		[
			backlogHref,
			boardHref,
			formatMessage,
			getKeywords,
			hasBacklog,
			hasBoard,
			hasReports,
			hasTimeline,
			hideAndTransformNavigationCommands,
			reportsHref,
			timelineHref,
		],
	);

	return (
		<RegisterCommands
			registrationId={COMMAND_PALETTE_REGISTRY_IDS.PROJECT_NAVIGATION}
			commands={commands}
			deps={commands}
		/>
	);
};
